"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../lib/format";

interface PortfolioCategory {
  categoryId: string;
  name: string;
  netROI: number;
  evidenceQuality: string;
}

interface PortfolioChartProps {
  categories: PortfolioCategory[];
  onCategoryClick: (categoryId: string) => void;
}

const EVIDENCE_BAR_COLORS: Record<string, string> = {
  strong: "#059669",
  moderate: "#f59e0b",
  weak: "#ef4444",
};

export function PortfolioChart({
  categories,
  onCategoryClick,
}: PortfolioChartProps) {
  const data = categories.map((c) => ({
    name: c.name,
    categoryId: c.categoryId,
    netROI: c.netROI,
    fill:
      c.netROI < 0
        ? "#ef4444"
        : (EVIDENCE_BAR_COLORS[c.evidenceQuality] ?? "#6b7280"),
  }));

  const chartHeight = Math.max(300, data.length * 64 + 40);

  return (
    <div className="w-full" style={{ height: chartHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 40, left: 160, bottom: 20 }}
          barCategoryGap="30%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v: number) => formatCurrency(v)}
            axisLine={false}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={150}
            tick={{ fontSize: 13 }}
          />
          <Tooltip
            formatter={(value: unknown) => formatCurrency(Number(value))}
            labelFormatter={(label: unknown) => String(label)}
          />
          <ReferenceLine x={0} stroke="#9ca3af" strokeDasharray="3 3" />
          <Bar
            dataKey="netROI"
            radius={[0, 4, 4, 0]}
            barSize={32}
            cursor="pointer"
            onClick={(barData) => {
              const entry = barData as unknown as { categoryId?: string };
              if (entry.categoryId) onCategoryClick(entry.categoryId);
            }}
          >
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
